import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerImg from "../../assets/Button-Map-marker.png";
import positiveIcon from "../../assets/postive-cases.png";
import negativeIcon from "../../assets/negative-cases.png";

import {
  Box,
  CircularProgress,
  alpha,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";

/* ================= TYPES ================= */

export interface HealthCenter {
  _id: string;
  name: string;
  district: string;
  lat: number;
  lng: number;
  status: "Active" | "Offline";
  battery: string;
}

export interface Device {
  _id: string;
  batteryStatus: string;
  healthCenter: string;
  lat?: number;
  lng?: number;
}

export interface Test {
  _id: string;
  diagnosisResults: "Positive" | "Negative" | "Indeterminate";
  parasiteSpecies: string;
  drugResistance: string;
  healthCenter: string;
  timestamp: string;
  deviceId: string;
}

interface EnrichedDevice extends Device {
  tests: number;
  positiveCases: number;
  batteryLevel: number;
  speciesDistribution?: Record<string, number>;
  drugResistanceCases: number;
}

interface MapComponentProps {
  centers?: HealthCenter[];
}

/* ================= MARKER ================= */

const markerIcon = L.icon({
  iconUrl: markerImg,
  iconSize: [26, 30],
  iconAnchor: [13, 36],
  popupAnchor: [0, -28],
});

/* ================= FIT BOUNDS ================= */

const FitBounds: React.FC<{ points: [number, number][] }> = ({ points }) => {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (!fitted.current && points.length) {
      map.fitBounds(points, { padding: [45, 65] });
      fitted.current = true;
    }
  }, [points, map]);

  return null;
};

/* ================= COMPONENT ================= */

const MapComponent: React.FC<MapComponentProps> = ({ centers = [] }) => {
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [deviceData, setDeviceData] = useState<EnrichedDevice[]>([]);
  const [selectedDevice, setSelectedDevice] =
    useState<EnrichedDevice | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /* ---------- Fetch & enrich ---------- */
  const fetchAndCacheMapData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [testsRes, centersRes, devicesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/tests`),
        fetch(`${API_BASE_URL}/api/health-centers`),
        fetch(`${API_BASE_URL}/api/devices`),
      ]);

      const [tests, centersData, devices] = await Promise.all([
        testsRes.json(),
        centersRes.json(),
        devicesRes.json(),
      ]);

      const enriched: EnrichedDevice[] = devices.map((d: Device) => {
        const related = tests.filter(
          (t: Test) =>
            t.deviceId === d._id || t.healthCenter === d.healthCenter
        );

        const positives = related.filter(
          (t: Test) => t.diagnosisResults === "Positive"
        );

        const speciesCount: Record<string, number> = {};
        positives.forEach((t: Test) => {
          if (t.parasiteSpecies) {
            speciesCount[t.parasiteSpecies] =
              (speciesCount[t.parasiteSpecies] || 0) + 1;
          }
        });

        const speciesDistribution = Object.fromEntries(
          Object.entries(speciesCount).map(([k, v]) => [
            k,
            Math.round((v / (positives.length || 1)) * 100),
          ])
        );

        const center = centersData.find(
          (c: HealthCenter) => c.name === d.healthCenter
        );

        const batteryLevel =
          parseInt(d.batteryStatus?.replace("%", "") || "75", 10);

        return {
          ...d,
          lat: d.lat || center?.lat,
          lng: d.lng || center?.lng,
          tests: related.length,
          positiveCases: positives.length,
          drugResistanceCases: related.filter(
            (t: Test) => t.drugResistance?.toLowerCase() === "yes"
          ).length,
          batteryLevel,
          speciesDistribution:
            Object.keys(speciesCount).length
              ? speciesDistribution
              : undefined,
        };
      });

      setDeviceData(enriched);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchAndCacheMapData();
  }, [fetchAndCacheMapData]);

  /* ---------- FILTER ---------- */
  const filteredDeviceData = useMemo(() => {
    if (!centers.length) return deviceData;
    const allowed = new Set(centers.map((c) => c.name));
    return deviceData.filter((d) => allowed.has(d.healthCenter));
  }, [deviceData, centers]);

  const points: [number, number][] = filteredDeviceData
    .filter((d) => d.lat && d.lng)
    .map((d) => [d.lat!, d.lng!]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
      <MapContainer
        center={[1.3733, 32.2903]}
        zoom={6}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution=""
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds points={points} />

        {filteredDeviceData.map(
          (d) =>
            d.lat &&
            d.lng && (
              <Marker
                key={d._id}
                position={[d.lat, d.lng]}
                icon={markerIcon}
                eventHandlers={{
                  click: () => setSelectedDevice(d),
                }}
              />
            )
        )}

        {selectedDevice && (
          <Popup
            position={[selectedDevice.lat!, selectedDevice.lng!]}
            closeButton={false}
            closeOnClick={false}
            offset={[0, -26]}
            // ─── FIX FOR STRETCHING ───
            maxWidth={290}           // Caps the popup width (includes tip & shadow)
            minWidth={240}           // Prevents collapsing too narrow
            autoPan={true}
            autoPanPadding={[20, 60]}
          >
            <Box
              sx={{
                width: 265,
                minWidth: 240,
                maxWidth: 280,
                bgcolor: "#1f2b3a",
                color: "#ffffff",
                borderRadius: 3,
                p: 1.4,
                boxShadow: "0 10px 24px rgba(0,0,0,0.55)",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Stack direction="row" justifyContent="space-between" mb={1.2}>
                <Typography fontWeight={600} fontSize="0.9rem">
                  {selectedDevice.healthCenter}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setSelectedDevice(null)}
                  sx={{ color: "#9aa4b2", p: 0.3 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>

              {/* Meta */}
              <Stack spacing={0.6} mb={1.3}>
                <MetaRow
                  label="Device ID"
                  value={selectedDevice._id}
                  valueColor="#91969E"
                />
                <MetaRow
                  label="District"
                  value={
                    centers.find(
                      (c) => c.name === selectedDevice.healthCenter
                    )?.district || "N/A"
                  }
                />
                <MetaRow
                  label="Status"
                  value="Active Cases"
                  valueColor="#91969E"
                />
              </Stack>

              {/* Stats (COMPACT) */}
              <Stack spacing={0.8} mb={1.3}>
                <Stack direction="row" spacing={0.8}>
                  <StatBox title="Tests" value={selectedDevice.tests} />
                  <StatBox
                    title="Positive"
                    value={selectedDevice.positiveCases}
                    valueColor="#91969E"
                    icon={positiveIcon}
                  />
                </Stack>

                <Stack direction="row" spacing={0.8}>
                  <StatBox
                    title="Negative"
                    value={
                      selectedDevice.tests -
                      selectedDevice.positiveCases
                    }
                    valueColor="#91969E"
                    icon={negativeIcon}
                  />
                  <StatBox
                    title="Drug Resistance"
                    value={selectedDevice.drugResistanceCases}
                  />
                </Stack>
              </Stack>

              {/* Species */}
              <Typography fontSize="0.78rem" fontWeight={600} mb={0.6}>
                Species Distribution:
              </Typography>

              {selectedDevice.speciesDistribution ? (
                <Stack spacing={0.5}>
                  {Object.entries(
                    selectedDevice.speciesDistribution
                  ).map(([species, percent]) => (
                    <Stack
                      key={species}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography fontSize="0.72rem" color="#cfd8dc">
                        {species}
                      </Typography>
                      <Typography fontSize="0.72rem" color="#cfd8dc">
                        {percent}%
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography
                  fontSize="0.7rem"
                  color="#9aa4b2"
                  textAlign="center"
                >
                  No species data
                </Typography>
              )}
            </Box>
          </Popup>
        )}
      </MapContainer>

      {refreshing && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: alpha("#000", 0.6),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

/* ================= HELPERS ================= */

const MetaRow = ({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string | number;
  valueColor?: string;
}) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography fontSize="0.72rem" color="#9aa4b2">
      {label}:
    </Typography>
    <Typography
      fontSize="0.78rem"
      fontWeight={500}
      color={valueColor || "#ffffff"}
    >
      {value}
    </Typography>
  </Stack>
);

const StatBox = ({
  title,
  value,
  valueColor,
  icon,
}: {
  title: string;
  value: number;
  valueColor?: string;
  icon?: string;
}) => (
  <Box
    sx={{
      flex: 1,
      bgcolor: "#2a3748",
      borderRadius: 1.6,
      px: 0.9,
      py: 0.7,
    }}
  >
    <Typography fontSize="0.68rem" color="#9aa4b2">
      {title}
    </Typography>
    <Stack direction="row" spacing={0.4} alignItems="center">
      <Typography
        fontSize="1.1rem"
        fontWeight={600}
        color={valueColor || "#ffffff"}
      >
        {value}
      </Typography>
      {icon && (
        <img
          src={icon}
          alt={title}
          style={{ width: 14, height: 14 }}
        />
      )}
    </Stack>
  </Box>
);

export default MapComponent;